import { t } from "../lib/i18n";
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom'; // Import useNavigate
import api, { getProperties } from '../api';
import Tooltip from './Tooltip';

interface Property {
    id: string;
    name: string;
    address: string;
}

interface JobHistory {
    job_id: string;
    created_at: string;
    file_count: number;
    status: string;
}

export const BulkLeaseUploader: React.FC = () => {
    const navigate = useNavigate(); // Initialize useNavigate
    const [selectedFiles, setSelectedFiles] = useState<FileList | File[] | null>(null);
    const [uploadMode, setUploadMode] = useState<'files' | 'folder'>('files');
    const [propertyId, setPropertyId] = useState<string>('');
    const [properties, setProperties] = useState<Property[]>([]); // State for properties
    const [loadingProperties, setLoadingProperties] = useState(true);
    const [errorProperties, setErrorProperties] = useState<string | null>(null);
    const [message, setMessage] = useState('');
    const [uploading, setUploading] = useState(false);
    const [jobHistory, setJobHistory] = useState<JobHistory[]>([]);
    const [loadingHistory, setLoadingHistory] = useState(true);

    const handleModeChange = (mode: 'files' | 'folder') => {
        setUploadMode(mode);
        setSelectedFiles(null);
        setMessage('');
        const fileInput = document.getElementById('leaseFiles') as HTMLInputElement;
        if (fileInput) fileInput.value = '';
        const folderInput = document.getElementById('leaseFolder') as HTMLInputElement;
        if (folderInput) folderInput.value = '';
    };

    useEffect(() => {
        fetchProperties();
        fetchJobHistory();
    }, []);

    const fetchProperties = async () => {
        setLoadingProperties(true);
        setErrorProperties(null);
        try {
            const props = await getProperties();
            setProperties(props);
            if (props.length > 0) {
                setPropertyId(props[0].id); // Select the first property by default
            }
        } catch (err: any) {
            console.error('Error fetching properties:', err.response?.data || err.message);
            setErrorProperties('Could not fetch properties. Please try again later.');
        } finally {
            setLoadingProperties(false);
        }
    };

    const fetchJobHistory = async () => {
        setLoadingHistory(true);
        try {
            const response = await api.get('/bulk/jobs');
            setJobHistory(response.data);
        } catch (error) {
            console.error('Error fetching job history:', error);
            // Optionally set an error state here
        } finally {
            setLoadingHistory(false);
        }
    };

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.files) {
            setSelectedFiles(event.target.files);
        } else {
            setSelectedFiles(null);
        }
    };

    const handleUpload = async (event: React.FormEvent) => {
        event.preventDefault();
        if (!selectedFiles || selectedFiles.length === 0) {
            setMessage('Please select files to upload.');
            return;
        }
        if (!propertyId) {
            setMessage('Please select a Property.');
            return;
        }

        const allowedExtensions = ['.pdf', '.jpg', '.jpeg', '.png', '.gif'];
        const filesToSend: File[] = [];
        for (let i = 0; i < selectedFiles.length; i++) {
            const file = selectedFiles[i];
            const ext = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
            if (allowedExtensions.includes(ext)) {
                filesToSend.push(file);
            }
        }

        if (filesToSend.length === 0) {
            setMessage('Please select valid files to upload (.pdf, .jpg, .jpeg, .png, .gif).');
            return;
        }

        setUploading(true);
        setMessage('Uploading files... This process may take some time depending on the number and size of documents. Feel free to navigate away; the process will continue in the background.');
        const formData = new FormData();
        formData.append('property_id', propertyId);
        for (let i = 0; i < filesToSend.length; i++) {
            formData.append('files', filesToSend[i]);
        }

        try {
            const response = await api.post('/bulk/leases', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });
            setMessage(response.data.message + ` Job ID: ${response.data.job_id}`);
            setSelectedFiles(null);
            const fileInput = document.getElementById('leaseFiles') as HTMLInputElement;
            if (fileInput) fileInput.value = '';
            const folderInput = document.getElementById('leaseFolder') as HTMLInputElement;
            if (folderInput) folderInput.value = '';
            navigate(`/bulk-leases/${response.data.job_id}`); // Navigate to the validator page
        } catch (error: any) {
            console.error('Error uploading bulk leases:', JSON.stringify(error));
            setMessage(error.response?.data?.detail?.[0]?.msg || error.response?.data?.[0]?.msg || error.message || 'An error occurred during bulk upload.');
        } finally {
            setUploading(false);
        }
    };

    if (loadingProperties) {
        return <div className="container mx-auto p-4">{t("bulkLeaseUploader.k1")}</div>;
    }

    if (errorProperties) {
        return <div className="container mx-auto p-4 text-danger">{t("bulkLeaseUploader.k2")} {errorProperties}</div>;
    }

    return (
        <div className="container mx-auto p-4 bg-bg-primary min-h-screen">
            <div className="flex items-center mb-6">
                <h1 className="text-3xl font-bold text-text-secondary">{t("bulkLeaseUploader.k3")}</h1>
                <Tooltip text="This page allows you to upload multiple lease documents at once. The tool is highly versatile, capable of processing various formats including standard PDFs, image files (such as JPEG, PNG, and GIF), and even scanned PDFs or images from any type of scanner. Whether it's a tenant PDF or an image type, the system intelligently extracts information. The system will process them in the background. Please note that processing time may vary depending on the file size, file type, and the complexity of the document. This is the most efficient way to add a large number of leases to your portfolio." />
            </div>

            {message && <p className={`mb-4 text-center p-2 rounded ${(message.includes('Started processing') || message.includes('Uploading files...')) ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>{message}</p>}

            <form onSubmit={handleUpload} className="mb-6 bg-bg-secondary p-4 rounded shadow border border-border-primary">
                <div className="mb-4">
                    <label htmlFor="propertyId" className="block text-text-secondary text-sm font-bold mb-2">{t("bulkLeaseUploader.k4")}</label>
                    <select
                        id="propertyId"
                        value={propertyId}
                        onChange={(e) => setPropertyId(e.target.value)}
                        required
                        disabled={uploading || properties.length === 0}
                        className="shadow appearance-none border border-border-primary rounded w-full py-2 px-3 text-text-primary leading-tight focus:outline-none focus:shadow-outline mb-4"
                    >
                        <option value="" disabled>{t("bulkLeaseUploader.k5")}</option>
                        {properties.map(prop => (
                            <option key={prop.id} value={prop.id}>
                                {prop.name} - {prop.address}
                            </option>
                        ))}
                    </select>
                    {properties.length === 0 && !loadingProperties && (
                        <p className="text-sm text-red-500 mt-1">{t("bulkLeaseUploader.k6")}</p>
                    )}
                </div>

                {/* Upload Mode Selector Toggle */}
                <div className="mb-6 border-b border-border-primary flex gap-4">
                    <button
                        type="button"
                        onClick={() => handleModeChange('files')}
                        className={`py-2 px-4 font-semibold border-b-2 transition-all duration-200 !bg-transparent !shadow-none !border-t-0 !border-l-0 !border-r-0 rounded-none ${uploadMode === 'files' ? 'border-primary text-primary' : 'border-transparent text-text-secondary hover:text-text-primary'}`}
                    >
                        {t("bulkLeaseUploader.k7")} </button>
                    <button
                        type="button"
                        onClick={() => handleModeChange('folder')}
                        className={`py-2 px-4 font-semibold border-b-2 transition-all duration-200 !bg-transparent !shadow-none !border-t-0 !border-l-0 !border-r-0 rounded-none ${uploadMode === 'folder' ? 'border-primary text-primary' : 'border-transparent text-text-secondary hover:text-text-primary'}`}
                    >
                        {t("bulkLeaseUploader.k8")} </button>
                </div>

                {uploadMode === 'files' ? (
                    <div className="mb-6">
                        <label htmlFor="leaseFiles" className="block text-text-secondary text-sm font-bold mb-2">{t("bulkLeaseUploader.k9")}</label>
                        <input
                            type="file"
                            id="leaseFiles"
                            multiple
                            accept=".pdf,.jpg,.jpeg,.png,.gif"
                            onChange={handleFileChange}
                            disabled={uploading}
                            className="shadow appearance-none border border-border-primary rounded w-full py-2.5 px-3 text-text-primary leading-tight focus:outline-none focus:shadow-outline bg-bg-primary"
                        />
                    </div>
                ) : (
                    <div className="mb-6">
                        <label htmlFor="leaseFolder" className="block text-text-secondary text-sm font-bold mb-2">{t("bulkLeaseUploader.k10")}</label>
                        <input
                            type="file"
                            id="leaseFolder"
                            multiple
                            {...({
                                webkitdirectory: "",
                                directory: ""
                            } as any)}
                            onChange={handleFileChange}
                            disabled={uploading}
                            className="shadow appearance-none border border-border-primary rounded w-full py-2.5 px-3 text-text-primary leading-tight focus:outline-none focus:shadow-outline bg-bg-primary"
                        />
                    </div>
                )}

                <div className="flex items-center gap-4">
                    <button 
                        type="submit" 
                        disabled={uploading || properties.length === 0} 
                        className="bg-primary hover:bg-primary-dark text-white px-6 py-2.5 rounded font-medium transition duration-200 shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {uploading ? 'Uploading...' : t("bulkLeaseUploader.k11")}
                    </button>
                    {selectedFiles && selectedFiles.length > 0 && (
                        <span className="text-sm text-text-secondary">
                            {t("bulkLeaseUploader.k12")} {selectedFiles.length} {t("bulkLeaseUploader.k13")}{selectedFiles.length > 1 ? 's' : ''}
                        </span>
                    )}
                </div>
            </form>

            <div className="mt-12">
                <h2 className="text-2xl font-bold mb-4 text-text-secondary">{t("bulkLeaseUploader.k14")}</h2>
                {loadingHistory ? (
                    <p>{t("bulkLeaseUploader.k15")}</p>
                ) : jobHistory.length === 0 ? (
                    <p className="text-text-secondary">{t("bulkLeaseUploader.k16")}</p>
                ) : (
                    <div className="overflow-x-auto bg-bg-secondary p-4 rounded-lg shadow border border-border-primary">
                        <table className="min-w-full divide-y divide-border-primary">
                            <thead className="bg-bg-tertiary">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">{t("bulkLeaseUploader.k17")}</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">{t("bulkLeaseUploader.k18")}</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">{t("bulkLeaseUploader.k19")}</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">{t("bulkLeaseUploader.k20")}</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">{t("bulkLeaseUploader.k21")}</th>
                                </tr>
                            </thead>
                            <tbody className="bg-bg-secondary divide-y divide-border-primary">
                                {jobHistory.map((job) => (
                                    <tr key={job.job_id}>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-text-primary">{job.job_id.substring(0, 8)}...</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-text-secondary">{new Date(job.created_at).toLocaleString()}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-text-secondary">{job.file_count}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-text-secondary">{job.status}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                            <button onClick={() => navigate(`/bulk-leases/${job.job_id}`)} className="text-indigo-600 hover:text-indigo-900">
                                                {t("bulkLeaseUploader.k22")} </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

